import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { Router, NavigationStart } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Alert, AlertType } from '@app/_models';
import { AlertService, LoggerService } from '@app/_services';

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.sass'],
  standalone: true,
  imports: [CommonModule, MatCardModule]
})
export class AlertComponent implements OnInit, OnDestroy {
  @Input() id = 'default-alert';
  @Input() fade = true;

  private destroy$ = new Subject<void>();
  alerts: Alert[] = [];

  constructor(
    private router: Router,
    private alertService: AlertService,
    private logger: LoggerService
  ) { }

  ngOnInit(): void {
    // subscribe to new alert notifications
    this.alertService.onAlert(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: alert => {
          // clear alerts when an empty alert is received
          if (!alert.message) {
            // filter out alerts without 'keepAfterRouteChange' flag
            this.alerts = this.alerts.filter(x => x.keepAfterRouteChange);

            // remove 'keepAfterRouteChange' flag on the rest
            this.alerts.forEach(x => delete x.keepAfterRouteChange);
            return;
          }

          // add alert to array
          this.alerts.push(alert);

          // auto close alert if required
          if (alert.autoClose) {
            setTimeout(() => this.removeAlert(alert), 3000);
          }
        },
        error: err => this.logger.error('onAlert failed:', err)
      });

    // clear alerts on location change
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: event => {
          if (event instanceof NavigationStart) {
            this.alertService.clear(this.id);
          }
        },
        error: err => this.logger.error('router events failed:', err)
      });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  removeAlert(alert: Alert) {
    // check if already removed to prevent error on auto close
    if (!this.alerts.includes(alert)) return;

    if (this.fade) {
        // fade out alert
        //this.alerts.find(x => x === alert).fade = true; //FIXME: error in find

        // remove alert after faded out
        setTimeout(() => {
            this.alerts = this.alerts.filter(x => x !== alert);
        }, 250);
    } else {
        // remove alert
        this.alerts = this.alerts.filter(x => x !== alert);
    }
  }

  cssClass(alert: Alert) {
    if (!alert) return;

    const classes = ['alert', 'alert-dismissable', 'mt-4', 'container'];
            
    const alertTypeClass = {
        [AlertType.Success]: 'alert alert-success',
        [AlertType.Error]: 'alert alert-danger',
        [AlertType.Info]: 'alert alert-info',
        [AlertType.Warning]: 'alert alert-warning'
    }

    classes.push(alertTypeClass[alert.type]);

    if (alert.fade) {
        classes.push('fade');
    }

    return classes.join(' ');
  }

  // TrackBy function for performance optimization
  trackByAlertId(index: number, alert: Alert): string {
    return alert.id;
  }

}
